
import React, { useState } from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { UserMinus } from 'lucide-react';
import { toast } from 'sonner';
import { familyMembershipService } from '@/services/family';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { FamilyMember } from '@/types/database';

type FamilyMembersListProps = {
  familyMembers: FamilyMember[];
  isFamilyAdmin: boolean;
  userId?: string;
  familyId: string | null;
  familyAdminId?: string;
};

export const FamilyMembersList: React.FC<FamilyMembersListProps> = ({
  familyMembers,
  isFamilyAdmin,
  userId,
  familyId,
  familyAdminId
}) => {
  const [isRemovingMember, setIsRemovingMember] = useState(false);

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const handleRemoveFamilyMember = async (memberId: string) => {
    if (!isFamilyAdmin || memberId === userId) return;

    setIsRemovingMember(true);
    try {
      const result = await familyMembershipService.removeFamilyMember(memberId);
      
      if (result.success) {
        toast.success(result.message);
        // Reload the page to reflect the changes
        window.location.reload();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error removing family member:', error);
      toast.error('Failed to remove family member');
    } finally {
      setIsRemovingMember(false);
    }
  };

  if (!familyId) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        You are not part of a family yet.
      </div>
    );
  }

  if (familyMembers.length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        No family members found.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          {isFamilyAdmin && <TableHead className="w-[80px]">Actions</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {familyMembers.map((member) => (
          <TableRow key={member.id}>
            <TableCell className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs">
                  {getInitials(member.first_name, member.last_name)}
                </AvatarFallback>
              </Avatar>
              <span>{member.first_name} {member.last_name}</span>
              {member.id === userId && (
                <span className="ml-2 text-xs text-muted-foreground">(You)</span>
              )}
              {member.id === familyAdminId && member.id !== userId && (
                <span className="ml-2 text-xs text-muted-foreground">(Admin)</span>
              )}
            </TableCell>
            {isFamilyAdmin && (
              <TableCell>
                {member.id !== userId && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        className="text-destructive focus:text-destructive"
                        onClick={() => handleRemoveFamilyMember(member.id)}
                        disabled={isRemovingMember}
                      >
                        Remove from family
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
